<template>
  <div class="container mx-auto px-4 py-8">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Users</h1>
      <button
        @click="showModal = true"
        class="btn-primary"
        :disabled="isSaving"
      >
        Add User
      </button>
    </div>

    <DataTable
      :columns="columns"
      :data="usersState.data"
      :loading="usersState.loading"
      :error="usersState.error"
      :pagination="{
        currentPage: usersState.currentPage,
        totalPages: usersState.totalPages,
        totalItems: usersState.totalEntries,
        entriesPerPage: usersState.entriesPerPage,
        orderBy: usersState.orderBy,
        sortDirection: usersState.sortDirection
      }"
      @page-change="handlePageChange"
      @rowClick="showUserDetails"
    >
      <template #mfa="{ row }">
        <div class="flex items-center gap-2">
          <span
            v-if="row.mfa_enabled"
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
            title="User has enrolled an authenticator app"
          >Enrolled</span>
          <span
            v-else-if="row.mfa_required"
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
            title="Admin requires 2FA on next login — user will be forced through enrolment"
          >Required (pending)</span>
          <span
            v-else
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
          >Off</span>
        </div>
      </template>
      <template #actions="{ row }">
        <div class="flex flex-wrap gap-2">
          <button
            @click.stop="showUserDetails(row)"
            class="btn-secondary btn-sm"
            :disabled="isSaving"
          >
            Edit
          </button>
          <button
            @click.stop="resendInvitation(row)"
            class="btn-secondary btn-sm"
            :disabled="isSaving || resendingId === row.id"
            title="Resend invitation email with a fresh login token"
          >
            {{ resendingId === row.id ? 'Sending…' : 'Resend invite' }}
          </button>
          <button
            v-if="!row.mfa_required"
            @click.stop="setMfaRequired(row, true)"
            class="btn-secondary btn-sm"
            :disabled="isSaving || mfaBusyId === row.id"
            title="Force this user to enrol 2FA on their next sign in"
          >
            {{ mfaBusyId === row.id ? '…' : 'Require 2FA' }}
          </button>
          <button
            v-else
            @click.stop="setMfaRequired(row, false)"
            class="btn-secondary btn-sm"
            :disabled="isSaving || mfaBusyId === row.id"
            title="Drop the 2FA requirement for this user"
          >
            {{ mfaBusyId === row.id ? '…' : 'Un-require 2FA' }}
          </button>
          <button
            v-if="row.mfa_enabled"
            @click.stop="resetMfa(row)"
            class="btn-secondary btn-sm"
            :disabled="isSaving || mfaBusyId === row.id"
            title="Clear their authenticator secret (lost-phone recovery) — they'll enrol again on next login"
          >
            {{ mfaBusyId === row.id ? '…' : 'Reset 2FA' }}
          </button>
          <button
            @click.stop="deleteUser(row)"
            class="btn-danger btn-sm"
            :disabled="isSaving"
          >
            Delete
          </button>
        </div>
      </template>
    </DataTable>

    <!-- User Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" @click.self="closeModal">
      <div class="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 shadow-xl">
        <h2 class="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
          {{ selectedUser ? 'Edit User' : 'Add User' }}
        </h2>
        <form @submit.prevent="handleSubmit" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
            <input
              v-model="form.name"
              type="text"
              required
              class="input mt-1"
              :disabled="isSaving"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
            <input
              v-model="form.email"
              type="email"
              required
              class="input mt-1"
              :disabled="isSaving"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
            <select
              v-model="form.role"
              required
              class="input mt-1"
              :disabled="isSaving"
            >
              <option value="admin">Admin</option>
              <option value="edit">Edit</option>
              <option value="view">View</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Auth Method</label>
            <select
              v-model="form.auth_method"
              required
              class="input mt-1"
              :disabled="isSaving"
            >
              <option value="password">Password</option>
              <option value="oauth">OAuth</option>
              <option value="saml">SAML</option>
              <option value="email">Email</option>
            </select>
          </div>
          <div class="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              @click="closeModal"
              class="btn-secondary"
              :disabled="isSaving"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="btn-primary"
              :disabled="isSaving"
            >
              <span v-if="isSaving" class="inline-block animate-spin mr-2">⟳</span>
              {{ selectedUser ? 'Update' : 'Create' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <DeleteConfirmationModal
      :show="showDeleteModal"
      title="Confirm Delete User"
      message="Are you sure you want to delete this user? This action cannot be undone."
      :loading="isDeleting"
      @confirm="handleDeleteConfirm"
      @cancel="showDeleteModal = false"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import axios from 'axios'
import DataTable from '@/components/DataTable.vue'
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal.vue'
import { ErrorTypes, ErrorMessages } from '@/utils/errorHandler'

const getApiBase = () => {
  const envUrl = process.env.VUE_APP_API_URL || 'http://127.0.0.1:10000'
  const clean = envUrl.replace(/\/$/, '')
  return clean.endsWith('/api/v1') ? clean : `${clean}/api/v1`
}

export default {
  name: 'UsersView',
  components: {
    DataTable,
    DeleteConfirmationModal
  },
  setup() {
    const store = useStore()
    const showModal = ref(false)
    const showDeleteModal = ref(false)
    const selectedUser = ref(null)
    const userToDelete = ref(null)
    const isSaving = ref(false)
    const isDeleting = ref(false)
    const resendingId = ref(null)
    const currentTenantId = computed(() => store.state.currentTenantId)
    const usersState = computed(() => store.state.users || {
      data: [],
      loading: false,
      error: null,
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      entriesPerPage: 10,
      search: '',
      orderBy: 'id',
      sortDirection: 'asc'
    })

    const form = ref({
      name: '',
      email: '',
      role: 'view',
      auth_method: 'password'
    })

    const columns = [
      { key: 'name', label: 'Name', sortable: true },
      { key: 'email', label: 'Email', sortable: true },
      { key: 'role', label: 'Role', sortable: true },
      { key: 'auth_method', label: 'Auth Method', sortable: true },
      { key: 'mfa', label: '2FA', slot: 'mfa' },
      { key: 'actions', label: 'Actions', slot: 'actions' }
    ]

    const mfaBusyId = ref(null)
    const setMfaRequired = async (row, required) => {
      if (!currentTenantId.value) return
      try {
        mfaBusyId.value = row.id
        await axios.put(
          `${getApiBase()}/admin/tenants/${currentTenantId.value}/users/${row.id}/mfa-required`,
          { required },
          { headers: { Authorization: `Bearer ${store.state.token}` } }
        )
        store.dispatch('notification/showNotification', {
          type: 'success',
          message: required
            ? `2FA is now required for ${row.email} on next login`
            : `2FA requirement removed for ${row.email}`,
          duration: 3000
        })
        await store.dispatch('users/fetchData', {
          tenantId: currentTenantId.value,
          page: usersState.value.currentPage, entriesPerPage: usersState.value.entriesPerPage,
          search: '', orderBy: 'id', sortDirection: 'asc'
        })
      } catch (err) {
        store.dispatch('notification/showNotification', {
          type: 'error',
          message: err.response?.data?.error || 'Failed to update 2FA policy',
          duration: 5000
        })
      } finally {
        mfaBusyId.value = null
      }
    }
    const resetMfa = async (row) => {
      if (!currentTenantId.value) return
      if (!window.confirm(`Reset 2FA for ${row.email}? They will have to enrol an authenticator again on next login.`)) return
      try {
        mfaBusyId.value = row.id
        await axios.post(
          `${getApiBase()}/admin/tenants/${currentTenantId.value}/users/${row.id}/mfa-reset`,
          {},
          { headers: { Authorization: `Bearer ${store.state.token}` } }
        )
        store.dispatch('notification/showNotification', {
          type: 'success',
          message: `2FA reset for ${row.email} — they will be prompted to enrol again.`,
          duration: 3000
        })
        await store.dispatch('users/fetchData', {
          tenantId: currentTenantId.value,
          page: usersState.value.currentPage, entriesPerPage: usersState.value.entriesPerPage,
          search: '', orderBy: 'id', sortDirection: 'asc'
        })
      } catch (err) {
        store.dispatch('notification/showNotification', {
          type: 'error',
          message: err.response?.data?.error || 'Failed to reset 2FA',
          duration: 5000
        })
      } finally {
        mfaBusyId.value = null
      }
    }

    const showUserDetails = (row) => {
      try {
        selectedUser.value = {
          id: row.id,
          name: row.name,
          email: row.email,
          role: row.role,
          auth_method: row.auth_method
        }
        form.value = {
          name: row.name,
          email: row.email,
          role: row.role,
          auth_method: row.auth_method
        }
        showModal.value = true
      } catch (error) {
        store.dispatch('notification/showNotification', {
          type: 'error',
          message: ErrorMessages[ErrorTypes.UNKNOWN].load || 'Failed to load user details',
          duration: 5000
        })
      }
    }

    const closeModal = () => {
      showModal.value = false
      selectedUser.value = null
      form.value = {
        name: '',
        email: '',
        role: 'view',
        auth_method: 'password'
      }
    }

    const handleSubmit = async () => {
      if (!currentTenantId.value) {
        store.dispatch('notification/showNotification', {
          type: 'error',
          message: ErrorMessages[ErrorTypes.VALIDATION].tenant || 'No tenant selected',
          duration: 5000
        })
        return
      }

      try {
        isSaving.value = true
        const data = {
          name: form.value.name,
          email: form.value.email,
          role: form.value.role,
          auth_method: form.value.auth_method
        }

        if (selectedUser.value) {
          await store.dispatch('users/updateData', {
            tenantId: currentTenantId.value,
            id: selectedUser.value.id,
            data
          })
        } else {
          await store.dispatch('users/createData', {
            tenantId: currentTenantId.value,
            data
          })
        }

        // Refresh the data after successful save
        await store.dispatch('users/fetchData', {
          tenantId: currentTenantId.value,
          page: 1,
          entriesPerPage: 10,
          search: '',
          orderBy: 'id',
          sortDirection: 'asc'
        })

        store.dispatch('notification/showNotification', {
          type: 'success',
          message: `User ${selectedUser.value ? 'updated' : 'created'} successfully`,
          duration: 3000
        })

        closeModal()
      } catch (error) {
        const errorType = error.response?.status === 403 ? ErrorTypes.AUTHORIZATION : 
                         error.response?.status === 400 ? ErrorTypes.VALIDATION : 
                         ErrorTypes.UNKNOWN
        
        const action = selectedUser.value ? 'update' : 'create'
        const message = ErrorMessages[errorType][action] || 
                       `Failed to ${action} user: ${error.response?.data?.message || 'Unknown error'}`
        
        store.dispatch('notification/showNotification', {
          type: 'error',
          message,
          duration: 5000
        })
      } finally {
        isSaving.value = false
      }
    }

    const deleteUser = (row) => {
      userToDelete.value = row
      showDeleteModal.value = true
    }

    const resendInvitation = async (row) => {
      if (!currentTenantId.value) return
      try {
        resendingId.value = row.id
        await axios.post(
          `${getApiBase()}/admin/tenants/${currentTenantId.value}/users/${row.id}/resend-invitation`,
          {},
          { headers: { Authorization: `Bearer ${store.state.token}` } }
        )
        store.dispatch('notification/showNotification', {
          type: 'success',
          message: `Invitation email resent to ${row.email}`,
          duration: 3000
        })
      } catch (err) {
        store.dispatch('notification/showNotification', {
          type: 'error',
          message: err.response?.data?.error || 'Failed to resend invitation',
          duration: 5000
        })
      } finally {
        resendingId.value = null
      }
    }

    const handleDeleteConfirm = async () => {
      if (!currentTenantId.value || !userToDelete.value) {
        store.dispatch('notification/showNotification', {
          type: 'error',
          message: ErrorMessages[ErrorTypes.VALIDATION].tenant || 'No tenant selected',
          duration: 5000
        })
        return
      }

      try {
        isDeleting.value = true
        await store.dispatch('users/deleteData', {
          tenantId: currentTenantId.value,
          id: userToDelete.value.id
        })

        // Refresh the data after successful deletion
        await store.dispatch('users/fetchData', {
          tenantId: currentTenantId.value,
          page: 1,
          entriesPerPage: 10,
          search: '',
          orderBy: 'id',
          sortDirection: 'asc'
        })

        store.dispatch('notification/showNotification', {
          type: 'success',
          message: 'User deleted successfully',
          duration: 3000
        })

        if (selectedUser.value?.id === userToDelete.value.id) {
          closeModal()
        }

        showDeleteModal.value = false
        userToDelete.value = null
      } catch (error) {
        const errorType = error.response?.status === 403 ? ErrorTypes.AUTHORIZATION : 
                         error.response?.status === 400 ? ErrorTypes.VALIDATION : 
                         ErrorTypes.UNKNOWN
        
        const message = ErrorMessages[errorType].delete || 
                       `Failed to delete user: ${error.response?.data?.message || 'Unknown error'}`
        
        store.dispatch('notification/showNotification', {
          type: 'error',
          message,
          duration: 5000
        })
      } finally {
        isDeleting.value = false
      }
    }

    const handlePageChange = async (params) => {
      if (!currentTenantId.value) {
        store.commit('setError', 'No tenant selected')
        return
      }

      try {
        await store.dispatch('users/fetchData', {
          tenantId: currentTenantId.value,
          ...params
        })
      } catch (error) {
        store.commit('setError', error.message || 'Failed to fetch users')
      }
    }

    // Watch for tenant changes
    watch(currentTenantId, (newTenantId) => {
      if (newTenantId) {
        handlePageChange({
          page: 1,
          entriesPerPage: usersState.value.entriesPerPage,
          search: usersState.value.search,
          orderBy: usersState.value.orderBy,
          sortDirection: usersState.value.sortDirection
        })
      }
    })

    onMounted(() => {
      if (currentTenantId.value) {
        handlePageChange({
          page: 1,
          entriesPerPage: usersState.value.entriesPerPage,
          search: usersState.value.search,
          orderBy: usersState.value.orderBy,
          sortDirection: usersState.value.sortDirection
        })
      }
    })

    return {
      columns,
      showModal,
      showDeleteModal,
      selectedUser,
      userToDelete,
      form,
      isSaving,
      isDeleting,
      showUserDetails,
      closeModal,
      handleSubmit,
      deleteUser,
      handleDeleteConfirm,
      handlePageChange,
      resendInvitation,
      resendingId,
      setMfaRequired,
      resetMfa,
      mfaBusyId,
      usersState,
      currentTenantId
    }
  }
}
</script>